/**
 * frontend/AuthScreen.jsx
 * SoundDAO — Passwordless entry point.
 *
 * Primary path:  Magic link via Supabase Auth (email OTP)
 * Secondary path: Connect wallet (MetaMask) — anonymous session + wallet link
 *
 * Wallet flow (Fix A):
 *   1. Request wallet address from MetaMask
 *   2. supabase.auth.signInAnonymously() → real session, no fake email
 *   3. Link the wallet with a signature (lib/walletLink.js): server nonce →
 *      personal_sign → PATCH /api/users/link-wallet, verified server-side
 *      (Prompt 1.5). Best-effort — declining to sign does not block sign-in.
 *   4. onAuthStateChange fires → useAuth → App routes to /onboarding automatically
 */

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabaseClient';
import { linkWalletWithSignature } from './lib/walletLink';
import { getPendingClaim } from './lib/archiveApi';
import { readReferralCode } from './lib/referral';
import { useInviter, inviteBannerText } from './hooks/useInviter';
import Turnstile, { TURNSTILE_SITE_KEY } from './components/Turnstile';
import './AuthScreen.css';
import BrandMark from './components/BrandMark';

// Supabase's email OTP length is a project setting (6–10 digits); it currently sends 8.
export const OTP_MIN = 6;
export const OTP_MAX = 10;
// Supabase refuses a second email to the same address inside 60 s.
export const RESEND_COOLDOWN_S = 60;
export const RATE_LIMIT_MESSAGE = 'Too many requests — wait a minute and try again.';
export const CAPTCHA_MESSAGE = 'The security check failed — refresh the page and try again.';
export const CAPTCHA_LOAD_MESSAGE = "Couldn't load the security check. Check your connection (or turn off a blocker for this site) and refresh.";

const REDIRECT_URL = `${window.location.origin}/auth/callback`;

/** Supabase rate-limit errors ("For security purposes, you can only request this after…",
 *  "Email rate limit exceeded", HTTP 429) → friendly copy; anything else → `fallback`. */
export function friendlyAuthError(err, fallback) {
  const msg = String(err?.message || '');
  if (err?.status === 429 || /for security purposes|rate limit/i.test(msg)) return RATE_LIMIT_MESSAGE;
  if (/captcha/i.test(msg)) return CAPTCHA_MESSAGE;
  return fallback;
}

export default function AuthScreen() {
  const [email,       setEmail]       = useState('');
  const [sent,        setSent]        = useState(false);
  // Sprint 6-Zero M6 item 9 — OTP state (shown by default since registration fix 5)
  const [otp,         setOtp]         = useState('');
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  // Resend (registration fix 5): epoch ms until which "Resend link" is disabled, a ticking clock
  // for the countdown, and the resend outcome line.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now,           setNow]           = useState(() => Date.now());
  const [resendNote,    setResendNote]    = useState(null);
  // Referral context (registration fix 2): the stored code is normalised and checked with the
  // backend; a valid one shows who invited you, an unknown one is removed and shows nothing.
  const [referralCode] = useState(() => readReferralCode());
  const inviter = useInviter(referralCode);
  // Bot check (Cloudflare Turnstile, components/Turnstile.jsx). Only when the build has a site
  // key; Supabase then rejects sign-in requests without a valid, single-use token.
  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaReady = !TURNSTILE_SITE_KEY || Boolean(captchaToken);
  // Hand out the current token once, then reset the widget for the next request.
  const spendCaptcha = () => {
    const token = captchaToken;
    if (TURNSTILE_SITE_KEY) { setCaptchaToken(null); captchaRef.current?.reset(); }
    return token;
  };
  const captchaWidget = (
    <Turnstile
      ref={captchaRef}
      onToken={(t) => { setCaptchaToken(t); setCaptchaFailed(false); }}
      onExpire={() => setCaptchaToken(null)}
      onError={() => { setCaptchaToken(null); setCaptchaFailed(true); }}
    />
  );

  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  useEffect(() => {
    if (!sent || cooldownUntil <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [sent, cooldownUntil]);

  // ── Magic link send ──────────────────────────────────────────────────────────
  // Registration fix 3: the invite code also goes into the new account's user_metadata
  // (Supabase applies options.data only at signup), so /onboard can credit the referrer even
  // when the magic link opens in a browser whose localStorage is empty. Claim signups never
  // carry a referral. Returns an error message, or null on success. Never throws.
  const sendLink = async (address) => {
    const code = getPendingClaim() ? null : readReferralCode();
    const captchaTokenForRequest = spendCaptcha();
    try {
      const { error: authErr } = await supabase.auth.signInWithOtp({
        email: address,
        options: {
          emailRedirectTo: REDIRECT_URL,
          ...(code ? { data: { referred_by: code } } : {}),
          ...(captchaTokenForRequest ? { captchaToken: captchaTokenForRequest } : {}),
        },
      });
      if (authErr) return friendlyAuthError(authErr, authErr.message || 'Could not send magic link. Please try again.');
      setCooldownUntil(Date.now() + RESEND_COOLDOWN_S * 1000);
      setNow(Date.now());
      return null;
    } catch (err) {
      return friendlyAuthError(err, 'Could not send magic link. Please try again.');
    }
  };

  const handleSendLink = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    const failure = await sendLink(email.trim().toLowerCase());
    setLoading(false);
    if (failure) setError(failure);
    else setSent(true);
  };

  const handleResend = async () => {
    if (loading || secondsLeft > 0 || !captchaReady) return;
    setResendNote(null);
    setLoading(true);
    const failure = await sendLink(email.trim().toLowerCase());
    setLoading(false);
    setResendNote(failure ? { error: true, text: failure } : { error: false, text: 'New link sent — check your inbox.' });
  };

  // ── Wallet connect ───────────────────────────────────────────────────────────
  // Creates a real Supabase anonymous session — no email, no OTP, no synthetic
  // addresses.  signInAnonymously() fires SIGNED_IN, which useAuth catches,
  // fetches the profile (404 → user=null), and navigates to /onboarding —
  // identical to the magic link path.
  const handleConnectWallet = async () => {
    setError(null);

    if (!captchaReady) {
      setError('One moment — the security check is still running.');
      return;
    }

    if (!window.ethereum) {
      setError('No wallet detected. Install MetaMask to connect with a wallet.');
      return;
    }

    try {
      setLoading(true);

      // 1. Request wallet address from MetaMask
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const address  = accounts[0].toLowerCase();

      // 2. Create anonymous Supabase session
      const anonToken = spendCaptcha();
      const { data: { session }, error: anonErr } = await supabase.auth.signInAnonymously(
        anonToken ? { options: { captchaToken: anonToken } } : undefined,
      );

      if (anonErr || !session) {
        setError(/captcha/i.test(anonErr?.message || '') ? CAPTCHA_MESSAGE : 'Sign-in failed: ' + (anonErr?.message || 'unknown error'));
        setLoading(false);
        return;
      }

      // 3. Link the wallet by PROVING ownership (Prompt 1.5): the wallet signs a
      //    single-use server nonce and the backend verifies the signer. The old
      //    flow posted the bare address with a body-supplied user_id, so any
      //    caller could attach any wallet to any account.
      //    Non-fatal: if the user declines to sign, they are still signed in
      //    and can link from Settings later.
      try {
        await linkWalletWithSignature(address);
      } catch (linkErr) {
        console.warn('[wallet] link-wallet failed:', linkErr.message);
      }

      // 5. useAuth's SIGNED_IN handler fires automatically, fetches the profile
      //    (404 for new users), and navigates to /onboarding.  No manual
      //    navigation needed here.
      setLoading(false);

    } catch (err) {
      setLoading(false);
      if (err.code === 4001) {
        setError('Wallet connection cancelled.');
      } else {
        setError('Wallet connection failed: ' + (err.message || 'unknown error'));
      }
    }
  };

  // ── OTP verification (Sprint 6-Zero M6 item 9) ───────────────────────────────
  // Verifies the emailed code (6–10 digits; Supabase currently sends 8) in THIS browser, so a mail app that opens a
  // different browser can no longer break sign-in. useAuth's SIGNED_IN handler
  // takes over from here exactly as it does for the magic link.
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < OTP_MIN || otpLoading) return;

    setOtpError(null);
    setOtpLoading(true);

    try {
      const { error: verifyErr } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type:  'email',
      });

      if (verifyErr) {
        setOtpError(friendlyAuthError(verifyErr,
          /expired|invalid/i.test(verifyErr.message || '')
            ? 'That code is invalid or has expired. Request a new link and try again.'
            : (verifyErr.message || 'Could not verify that code.')
        ));
        setOtpLoading(false);
        return;
      }
      // Success: onAuthStateChange fires SIGNED_IN and useAuth navigates away.
      // Deliberately leave the spinner running so nothing flashes mid-redirect.
    } catch (err) {
      setOtpError(friendlyAuthError(err, 'Could not verify that code. Please try again.'));
      setOtpLoading(false);
    }
  };

  // ── Sent state (email only) ──────────────────────────────────────────────────
  // Registration fix 5: the code entry is shown by default, right under the link sentence — it
  // works in any browser, unlike the link when the mail app opens a different one. Resend link
  // has a 60 s cooldown (Supabase rejects resends inside 60 s).
  if (sent) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-logo"><BrandMark /></div>
          <h1 className="auth-title">SoundDAO</h1>
          <div className="auth-sent">
            <div className="auth-sent-icon">✉️</div>
            <h2>Check your email</h2>
            <p>We sent a magic link to <strong>{email}</strong>.</p>

            <form className="auth-otp auth-otp-form" onSubmit={handleVerifyOtp}>
              <label className="auth-label" htmlFor="auth-otp-input">
                Or enter the code from the email — it works in any browser
              </label>
              <input
                id="auth-otp-input"
                className="auth-input auth-otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={OTP_MAX}
                placeholder="Code from email"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_MAX))}
                disabled={otpLoading}
              />
              <button
                type="submit"
                className="auth-btn-primary"
                disabled={otpLoading || otp.length < OTP_MIN}
              >
                {otpLoading ? 'Verifying…' : 'Verify code'}
              </button>
              {otpError && <p className="auth-error">{otpError}</p>}
            </form>

            <p className="auth-sent-hint">Your link expires in 10 minutes.</p>

            <div className="auth-sent-actions">
              <button
                type="button"
                className="auth-btn-ghost auth-resend"
                onClick={handleResend}
                disabled={loading || secondsLeft > 0 || !captchaReady}
              >
                {loading ? 'Sending…' : secondsLeft > 0 ? `Resend link in ${secondsLeft}s` : 'Resend link'}
              </button>
              <button
                type="button"
                className="auth-btn-ghost"
                onClick={() => { setSent(false); setEmail(''); setOtp(''); setOtpError(null); setResendNote(null); setCooldownUntil(0); }}
              >
                Use a different email
              </button>
            </div>
            {resendNote && (
              <p className={resendNote.error ? 'auth-error' : 'auth-resend-ok'} role="status">{resendNote.text}</p>
            )}
            {captchaWidget}
            {captchaFailed && <p className="auth-error">{CAPTCHA_LOAD_MESSAGE}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ────────────────────────────────────────────────────────────────
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-logo"><BrandMark /></div>
        <h1 className="auth-title">SoundDAO</h1>
        {inviter.status === 'valid' ? (
          <p className="auth-tagline auth-invited-by">{inviteBannerText(inviter.displayName)}</p>
        ) : (
          <p className="auth-tagline">{getPendingClaim() ? 'Sign in to claim your artist page' : 'Gamifying Fandom'}</p>
        )}

        <form className="auth-form" onSubmit={handleSendLink}>
          <label className="auth-label" htmlFor="email">
            Enter your email to get started
          </label>
          <input
            id="email"
            type="email"
            className="auth-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            autoFocus
          />
          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading || !email.trim() || !captchaReady}
          >
            {loading ? 'Sending…' : 'Send magic link ✨'}
          </button>
          {captchaWidget}
          {captchaFailed && <p className="auth-error">{CAPTCHA_LOAD_MESSAGE}</p>}
        </form>

        {error && <p className="auth-error">{error}</p>}

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          className="auth-btn-wallet"
          onClick={handleConnectWallet}
          disabled={loading || !captchaReady}
        >
          <span className="auth-wallet-icon">🦊</span>
          {loading ? 'Connecting…' : 'Connect wallet'}
        </button>

        <p className="auth-footnote">
          No password. No gas. Just music.
          <br />
          By continuing you agree to our <a href="/legal/terms">Terms</a> and <a href="/legal/privacy">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
